/**
 * Validação e manipulação de arquivos de imagem.
 *
 * Validação em 2 camadas (MIME + magic bytes) — MIME sozinho é frágil.
 *
 * Limite: 20 MB por arquivo (hard limit).
 */

const MAX_SIZE = 20 * 1024 * 1024; // 20 MB

const ALLOWED_MIME = new Set(['image/png', 'image/jpeg', 'image/webp']);

// Assinaturas de magic bytes (lê primeiros 12 bytes do arquivo).
// PNG:  89 50 4E 47 0D 0A 1A 0A  (8 bytes)
// JPEG: FF D8 FF                   (3 bytes — qualquer variante FF D8 FF)
// WebP: 52 49 46 46 ?? ?? ?? ?? 57 45 42 50  (RIFF + size + WEBP)

const PNG_SIG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIG = [0xff, 0xd8, 0xff];
const WEBP_SIG_RIFF = [0x52, 0x49, 0x46, 0x46]; // "RIFF"
const WEBP_SIG_WEBP = [0x57, 0x45, 0x42, 0x50]; // "WEBP"

/**
 * Valida tipo (MIME + magic bytes) de um arquivo.
 * @param {File} file
 * @returns {Promise<{ok: boolean, type?: string, error?: string}>}
 */
export async function validateType(file) {
  if (!file || !(file instanceof File) && !(file instanceof Blob)) {
    return { ok: false, error: 'Arquivo inválido' };
  }

  // 1. MIME type check
  const mime = file.type;
  if (!ALLOWED_MIME.has(mime)) {
    return { ok: false, error: `Formato .${extFromMime(mime) || '?'} não suportado` };
  }

  // 2. Magic bytes check
  // FileReader funciona em jsdom E em browsers reais (mais robusto que
  // file.arrayBuffer() direto, que falha em jsdom).
  const head = new Uint8Array(await readAsArrayBuffer(file.slice(0, 12)));
  if (head.byteLength < 4) {
    return { ok: false, error: 'Arquivo vazio ou corrompido' };
  }

  if (matchesPrefix(head, PNG_SIG)) {
    return { ok: true, type: 'image/png' };
  }
  if (matchesPrefix(head, JPEG_SIG)) {
    return { ok: true, type: 'image/jpeg' };
  }
  if (matchesPrefix(head, WEBP_SIG_RIFF) && head.length >= 12 && matchesAt(head, 8, WEBP_SIG_WEBP)) {
    return { ok: true, type: 'image/webp' };
  }

  return { ok: false, error: 'Conteúdo do arquivo não bate com a extensão' };
}

/**
 * Valida tamanho (≤ 20 MB).
 * @param {File} file
 * @returns {{ok: boolean, error?: string}}
 */
export function validateSize(file) {
  if (!file || typeof file.size !== 'number') {
    return { ok: false, error: 'Arquivo inválido' };
  }
  if (file.size > MAX_SIZE) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    return { ok: false, error: `Arquivo muito grande (${sizeMB} MB). Máximo 20 MB` };
  }
  if (file.size === 0) {
    return { ok: false, error: 'Arquivo vazio' };
  }
  return { ok: true };
}

/**
 * Garante nome único: se `name` já existe em `existing`, retorna
 * "name (2).ext", "name (3).ext", etc.
 *
 * @param {string} name
 * @param {Set<string> | string[]} existing
 * @returns {string}
 */
export function uniqueName(name, existing) {
  const set = existing instanceof Set ? existing : new Set(existing || []);
  if (!set.has(name)) return name;

  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';

  for (let i = 2; i < 10000; i++) {
    const candidate = `${stem} (${i})${ext}`;
    if (!set.has(candidate)) return candidate;
  }
  // Fallback: timestamp
  return `${stem}-${Date.now()}${ext}`;
}

/**
 * Lê um File/Blob como ArrayBuffer.
 * @param {File | Blob} file
 * @returns {Promise<ArrayBuffer>}
 */
export function fileToArrayBuffer(file) {
  return readAsArrayBuffer(file);
}

// --- helpers ---

/**
 * Lê Blob/File como ArrayBuffer via FileReader.
 * FileReader é o método mais portável: funciona em jsdom (que não
 * implementa Blob.arrayBuffer() consistentemente) E em todos os browsers.
 */
function readAsArrayBuffer(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

function matchesPrefix(bytes, sig) {
  if (bytes.length < sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (bytes[i] !== sig[i]) return false;
  }
  return true;
}

function matchesAt(bytes, offset, sig) {
  if (bytes.length < offset + sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (bytes[offset + i] !== sig[i]) return false;
  }
  return true;
}

function extFromMime(mime) {
  if (!mime) return '';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/jpeg') return 'jpg';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/gif') return 'gif';
  if (mime === 'image/bmp') return 'bmp';
  if (mime === 'image/svg+xml') return 'svg';
  return '';
}
